import { memo, PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react'

import { haptic } from '../../components/functions/haptic'
import {
  getHoldDurationMs,
  getRarityRevealConfig,
  RARITY_REVEAL_COMMON_WAIT,
  RARITY_REVEAL_HOLD_NEUTRAL_MS,
  RarityRevealPhase,
} from '../consts/rarityReveal'

type RarityRevealProps = {
  rarity: number
  imageSrc: string
  reducedMotion: boolean
  onDone: () => void
  onPhase?: (phase: RarityRevealPhase) => void
}

type Particle = {
  angle: number
  speed: number
  life: number
  size: number
  color: string
}

type HoldVisual = {
  pressing: boolean
  progress: number
}

const NEUTRAL = '#B07AD0'
const BACKGROUND = '#1E0029'
const HOLD_RING_RADIUS = 32
const HOLD_RING_Y_FACTOR = 0.7
const CRACK_CORE = '#FFFDF0'
const HOLD_INTRO_MS = 420

// 2–3 прямых свечёных трещины через всю капсулу (а не радиальные лапки):
// каждая стартует со своей задержкой `at` и растёт от центра в обе стороны.
const CRACK_FRACTURES = [
  { angle: -0.38, length: 96, at: 0 },
  { angle: 1.08, length: 66, at: 0.3 },
  { angle: 0.26, length: 80, at: 0.62 },
] as const

const clamp = (value: number) => Math.min(1, Math.max(0, value))
const easeOut = (value: number) => 1 - Math.pow(1 - clamp(value), 3)

const hexToRgb = (hex: string) => {
  const value = Number.parseInt(hex.slice(1), 16)
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  }
}

const mixColor = (from: string, to: string, progress: number) => {
  const a = hexToRgb(from)
  const b = hexToRgb(to)
  const k = clamp(progress)
  return `rgb(${Math.round(a.r + (b.r - a.r) * k)}, ${Math.round(a.g + (b.g - a.g) * k)}, ${Math.round(a.b + (b.b - a.b) * k)})`
}

const makeRandom = (seed: number) => {
  let value = seed >>> 0
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0
    return value / 4294967296
  }
}

const makeParticles = (count: number, rarity: number, primary: string, secondary: string) => {
  const random = makeRandom(0x1f2e3d4c + rarity * 97)
  return Array.from({ length: count }, (_, index): Particle => ({
    angle: random() * Math.PI * 2,
    speed: 60 + random() * 330,
    life: 700 + random() * 1500,
    size: 1.5 + random() * 4,
    color: index % 5 === 0 ? '#FFFFFF' : index % 2 === 0 ? primary : secondary,
  }))
}

const scheduleRevealHaptics = (
  rarity: number,
  schedule: (callback: () => void, delay: number) => void,
) => {
  if (rarity <= 1) {
    haptic('light')
    return
  }
  if (rarity === 2) {
    haptic('success')
    return
  }

  haptic('heavy')
  if (rarity >= 4) schedule(() => haptic('heavy'), rarity === 4 ? 120 : 150)
  if (rarity >= 5) schedule(() => haptic('heavy'), 230)
}

const RarityReveal = ({
  rarity,
  imageSrc,
  reducedMotion,
  onDone,
  onPhase,
}: RarityRevealProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const onDoneRef = useRef(onDone)
  const onPhaseRef = useRef(onPhase)
  const startedAtRef = useRef(0)
  const holdActiveRef = useRef(false)
  const startPressRef = useRef<() => void>(() => undefined)
  const releasePressRef = useRef<() => void>(() => undefined)
  const lastHoldUiRef = useRef<HoldVisual>({ pressing: false, progress: 0 })
  const lastHapticTickRef = useRef(0)
  const [phase, setPhase] = useState<RarityRevealPhase>('wait')
  const [holdVisual, setHoldVisual] = useState<HoldVisual>({ pressing: false, progress: 0 })

  onDoneRef.current = onDone
  onPhaseRef.current = onPhase

  useEffect(() => {
    let disposed = false
    let finished = false
    let holdResolved = false
    let animationFrame = 0
    const timers = new Set<number>()
    const config = getRarityRevealConfig(rarity)
    const normalizedRarity = Math.min(5, Math.max(1, Math.round(rarity)))
    const holdDurationMs = getHoldDurationMs(normalizedRarity)
    const tellAt = RARITY_REVEAL_COMMON_WAIT
    const crackAt = tellAt + config.tell
    const flashAt = crackAt + config.crack
    const revealAt = flashAt + config.flash
    const particles = makeParticles(config.parts, normalizedRarity, config.primary, config.secondary)
    const image = new Image()

    let accumulatedHold = 0
    let pressStartedAt: number | null = null
    let pressTimer = 0
    let holdStartedAt = 0

    const clearWork = () => {
      timers.forEach(timer => window.clearTimeout(timer))
      timers.clear()
      window.cancelAnimationFrame(animationFrame)
      animationFrame = 0
      image.onload = null
      image.onerror = null
    }

    const finish = () => {
      if (disposed || finished) return
      finished = true
      clearWork()
      onDoneRef.current()
    }

    if (reducedMotion) {
      finish()
      return () => {
        disposed = true
        clearWork()
      }
    }

    const emit = (nextPhase: RarityRevealPhase) => {
      if (disposed || finished) return
      setPhase(nextPhase)
      onPhaseRef.current?.(nextPhase)
    }

    const schedule = (callback: () => void, delay: number) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer)
        if (!disposed && !finished) callback()
      }, delay)
      timers.add(timer)
      return timer
    }

    const cancelPressTimer = () => {
      if (!pressTimer) return
      window.clearTimeout(pressTimer)
      timers.delete(pressTimer)
      pressTimer = 0
    }

    // Раскрытие после удержания продолжает тот же кадр по elapsed-времени,
    // на котором работает crack/flash/reveal ниже: startedAtRef сдвигается
    // так, будто фаза tell только что закончилась (аналог t = T_TELL + 1 в
    // референсном ScanDemo.jsx), поэтому расчёт по elapsed не дублируется.
    const finishHold = () => {
      if (disposed || finished || holdResolved) return
      holdResolved = true
      holdActiveRef.current = false
      cancelPressTimer()
      pressStartedAt = null
      startedAtRef.current = performance.now() - crackAt
      setHoldVisual({ pressing: false, progress: 1 })

      if (config.crack > 0) {
        emit('crack')
        scheduleRevealHaptics(normalizedRarity, schedule)
        if (config.flash > 0) schedule(() => emit('flash'), config.crack)
        schedule(() => emit('reveal'), config.crack + config.flash)
        schedule(finish, config.crack + config.flash + config.reveal)
      } else if (config.flash > 0) {
        emit('flash')
        schedule(() => emit('reveal'), config.flash)
        schedule(finish, config.flash + config.reveal)
      } else {
        emit('reveal')
        scheduleRevealHaptics(normalizedRarity, schedule)
        schedule(finish, config.reveal)
      }
    }

    const releasePress = () => {
      if (pressStartedAt == null) return
      accumulatedHold = Math.min(holdDurationMs, accumulatedHold + (Date.now() - pressStartedAt))
      pressStartedAt = null
      cancelPressTimer()
    }
    releasePressRef.current = releasePress

    const startPress = () => {
      if (disposed || finished || holdResolved || !holdActiveRef.current) return
      if (pressStartedAt != null) return
      pressStartedAt = Date.now()
      const remaining = Math.max(0, holdDurationMs - accumulatedHold)
      pressTimer = window.setTimeout(() => {
        timers.delete(pressTimer)
        pressTimer = 0
        accumulatedHold = holdDurationMs
        pressStartedAt = null
        finishHold()
      }, remaining)
      timers.add(pressTimer)
    }
    startPressRef.current = startPress

    const drawCapsule = (
      context: CanvasRenderingContext2D,
      cx: number,
      cy: number,
      elapsed: number,
      color: string,
      scale: number,
      showCracks: number,
    ) => {
      context.save()
      context.translate(cx, cy + Math.sin(elapsed / 380) * 6)
      context.scale(scale, scale)
      context.beginPath()
      context.arc(0, 0, 46, 0, Math.PI * 2)
      context.fillStyle = '#3D0055'
      context.fill()
      context.beginPath()
      context.arc(0, -12, 39, Math.PI, Math.PI * 2)
      context.fillStyle = 'rgba(111, 0, 153, .82)'
      context.fill()
      context.strokeStyle = color
      context.globalAlpha = 0.68
      context.lineWidth = 2
      context.beginPath()
      context.arc(0, 0, 46, 0, Math.PI * 2)
      context.stroke()
      context.beginPath()
      context.moveTo(-45, 4)
      context.lineTo(45, 4)
      context.stroke()

      if (showCracks > 0) {
        const fractureCount = normalizedRarity >= 4 ? 3 : 2
        const traceFractures = () => {
          for (let index = 0; index < fractureCount; index += 1) {
            const fracture = CRACK_FRACTURES[index]
            const local = clamp((showCracks - fracture.at) / 0.36)
            if (local <= 0.02) continue
            const half = (fracture.length / 2) * easeOut(local)
            const ox = Math.cos(fracture.angle)
            const oy = Math.sin(fracture.angle)
            for (const direction of [1, -1]) {
              context.moveTo(0, 0)
              for (let f = 0.22; f <= 1.001; f += 0.26) {
                const offset = Math.sin(f * 11 + index * 2.1) * 4.2 * f
                context.lineTo(
                  ox * half * direction * f - oy * offset,
                  oy * half * direction * f + ox * offset,
                )
              }
            }
          }
        }

        context.lineWidth = 7
        context.strokeStyle = color
        context.globalAlpha = 0.28
        context.beginPath()
        traceFractures()
        context.stroke()

        context.lineWidth = 2
        context.strokeStyle = CRACK_CORE
        context.globalAlpha = 0.92
        context.beginPath()
        traceFractures()
        context.stroke()

        context.globalAlpha = 1
      }
      context.restore()
    }

    const drawFrame = (now: number) => {
      if (disposed || finished) return
      const canvas = canvasRef.current
      const context = canvas?.getContext('2d')
      if (!canvas || !context) {
        animationFrame = window.requestAnimationFrame(drawFrame)
        return
      }

      const bounds = canvas.getBoundingClientRect()
      const width = Math.max(1, bounds.width || canvas.parentElement?.clientWidth || 280)
      const height = Math.max(1, bounds.height || canvas.parentElement?.clientHeight || 480)
      const pixelRatio = Math.min(2, window.devicePixelRatio || 1)
      const targetWidth = Math.round(width * pixelRatio)
      const targetHeight = Math.round(height * pixelRatio)
      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth
        canvas.height = targetHeight
      }
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
      context.clearRect(0, 0, width, height)

      const cx = width / 2
      const cy = height * 0.44

      if (holdActiveRef.current) {
        const holdIntro = holdStartedAt ? easeOut((now - holdStartedAt) / HOLD_INTRO_MS) : 1
        context.fillStyle = BACKGROUND
        context.fillRect(0, 0, width, height)

        const live = accumulatedHold
          + (pressStartedAt != null ? Math.max(0, Date.now() - pressStartedAt) : 0)
        const hp = Math.min(holdDurationMs, live)
        const hk = clamp(hp / holdDurationMs)
        const pressing = pressStartedAt != null
        const holdColor = hp > RARITY_REVEAL_HOLD_NEUTRAL_MS
          ? mixColor(
            NEUTRAL,
            config.primary,
            clamp((hp - RARITY_REVEAL_HOLD_NEUTRAL_MS) / Math.max(1, holdDurationMs - RARITY_REVEAL_HOLD_NEUTRAL_MS)),
          )
          : NEUTRAL

        const glowRadius = 150 * (0.75 + holdIntro * (-0.15 + hk * 0.5))
        const glow = context.createRadialGradient(cx, cy, 2, cx, cy, glowRadius)
        glow.addColorStop(0, holdColor)
        glow.addColorStop(1, 'rgba(0, 0, 0, 0)')
        context.globalAlpha = 0.72 + holdIntro * (-0.52 + hk * 0.5)
        context.fillStyle = glow
        context.fillRect(0, 0, width, height)
        context.globalAlpha = 1

        drawCapsule(context, cx, cy, now, holdColor, 1 + hk * 0.08, 0)

        // Кольцо прогресса вынесено ниже капсулы отдельным индикатором,
        // чтобы не перекрывать саму капсулу во время удержания.
        const ringCy = height * HOLD_RING_Y_FACTOR
        context.beginPath()
        context.arc(cx, ringCy, HOLD_RING_RADIUS, 0, Math.PI * 2)
        context.strokeStyle = 'rgba(255, 255, 255, .14)'
        context.lineWidth = 3
        context.stroke()
        if (hk > 0.002) {
          context.beginPath()
          context.arc(cx, ringCy, HOLD_RING_RADIUS, -Math.PI / 2, -Math.PI / 2 + hk * Math.PI * 2)
          context.strokeStyle = holdColor
          context.lineWidth = 5
          context.lineCap = 'round'
          context.stroke()
          context.lineCap = 'butt'
        }

        for (let index = 0; index < 9; index += 1) {
          const angle = index * 0.7 + now * (0.0005 + index * 0.00002) * (1 + hk * 2.5)
          const radius = 58 + index * 2 + hk * 16
          context.beginPath()
          context.arc(
            cx + Math.cos(angle) * radius,
            cy + Math.sin(angle) * radius * 0.55,
            1.5 + (index % 3) * 0.5,
            0,
            Math.PI * 2,
          )
          context.globalAlpha = 0.75
          context.fillStyle = holdColor
          context.fill()
        }
        context.globalAlpha = 1

        if (pressing && hp > RARITY_REVEAL_HOLD_NEUTRAL_MS) {
          const tickInterval = 260 - 170 * clamp(
            (hp - RARITY_REVEAL_HOLD_NEUTRAL_MS) / Math.max(1, holdDurationMs - RARITY_REVEAL_HOLD_NEUTRAL_MS),
          )
          if (now - lastHapticTickRef.current > tickInterval) {
            lastHapticTickRef.current = now
            haptic('light')
          }
        }

        if (
          pressing !== lastHoldUiRef.current.pressing
          || Math.abs(hk - lastHoldUiRef.current.progress) > 0.08
        ) {
          lastHoldUiRef.current = { pressing, progress: hk }
          setHoldVisual({ pressing, progress: hk })
        }

        animationFrame = window.requestAnimationFrame(drawFrame)
        return
      }

      const elapsed = Math.max(0, now - startedAtRef.current)
      const tellProgress = clamp((elapsed - tellAt) / config.tell)
      const crackProgress = config.crack ? clamp((elapsed - crackAt) / config.crack) : 0
      const revealProgress = clamp((elapsed - revealAt) / config.reveal)
      const activeColor = elapsed < tellAt
        ? NEUTRAL
        : mixColor(NEUTRAL, config.primary, easeOut(tellProgress))

      context.fillStyle = BACKGROUND
      context.fillRect(0, 0, width, height)

      const glowProgress = elapsed < tellAt
        ? 0.5 + clamp(elapsed / tellAt) * 0.25
        : 0.75 + tellProgress * 0.55 + revealProgress * 0.7
      const glow = context.createRadialGradient(cx, cy, 2, cx, cy, 150 * glowProgress)
      glow.addColorStop(0, activeColor)
      glow.addColorStop(1, 'rgba(0, 0, 0, 0)')
      context.globalAlpha = 0.72
      context.fillStyle = glow
      context.fillRect(0, 0, width, height)
      context.globalAlpha = 1

      if (elapsed < revealAt) {
        const shake = elapsed >= crackAt && config.crack > 0
          ? config.shake * (0.4 + crackProgress)
          : 0
        context.save()
        context.translate(
          Math.sin(elapsed * 0.19) * shake * 0.5,
          Math.cos(elapsed * 0.23) * shake * 0.5,
        )
        drawCapsule(
          context,
          cx,
          cy,
          elapsed,
          activeColor,
          1 + tellProgress * 0.06 + crackProgress * 0.12,
          crackProgress,
        )
        context.restore()

        const waitProgress = clamp(elapsed / tellAt)
        for (let index = 0; index < 9; index += 1) {
          const angle = index * 0.7 + elapsed * (0.0005 + index * 0.00002)
          const radius = 58 + index * 2 + tellProgress * 16
          context.beginPath()
          context.arc(
            cx + Math.cos(angle) * radius,
            cy + Math.sin(angle) * radius * 0.55,
            1.5 + (index % 3) * 0.5,
            0,
            Math.PI * 2,
          )
          context.globalAlpha = waitProgress * 0.8
          context.fillStyle = elapsed < tellAt ? '#FFFFFF' : activeColor
          context.fill()
        }
        context.globalAlpha = 1
      } else {
        context.save()
        const shake = config.shake * Math.max(0, 1 - revealProgress * 4)
        context.translate(
          Math.sin(elapsed * 0.31) * shake * 0.5,
          Math.cos(elapsed * 0.29) * shake * 0.5,
        )

        if (config.rays > 0) {
          context.save()
          context.translate(cx, cy)
          context.rotate(revealProgress * 0.5)
          context.globalAlpha = Math.min(1, revealProgress * 4) * (1 - revealProgress * 0.35)
          context.fillStyle = config.primary
          for (let index = 0; index < config.rays; index += 1) {
            const angle = (index / config.rays) * Math.PI * 2
            context.save()
            context.rotate(angle)
            context.beginPath()
            context.moveTo(12, -3)
            context.lineTo(Math.max(width, height) * 0.75, -12)
            context.lineTo(Math.max(width, height) * 0.75, 12)
            context.lineTo(12, 3)
            context.closePath()
            context.fill()
            context.restore()
          }
          context.restore()
        }

        for (let index = 0; index < 3; index += 1) {
          const waveProgress = clamp((elapsed - revealAt - index * 130) / 750)
          if (waveProgress <= 0 || waveProgress >= 1) continue
          context.beginPath()
          context.arc(cx, cy, 40 * (0.4 + easeOut(waveProgress) * 3.4), 0, Math.PI * 2)
          context.globalAlpha = (1 - waveProgress) * 0.75
          context.strokeStyle = config.primary
          context.lineWidth = 3
          context.stroke()
        }

        const seconds = (elapsed - revealAt) / 1000
        for (let index = 0; index < config.shards; index += 1) {
          const angle = (index / config.shards) * Math.PI * 2
          const speed = 90 + (index % 7) * 19
          const x = cx + Math.cos(angle) * speed * seconds
          const y = cy + Math.sin(angle) * speed * seconds + 140 * seconds * seconds
          context.save()
          context.translate(x, y)
          context.rotate(seconds * ((index % 2 ? 1 : -1) * 3))
          context.globalAlpha = Math.max(0, 1 - seconds * 1.4)
          context.fillStyle = '#6F0099'
          context.strokeStyle = config.primary
          context.beginPath()
          context.moveTo(0, 0)
          context.lineTo(16, -7)
          context.lineTo(11, 10)
          context.closePath()
          context.fill()
          context.stroke()
          context.restore()
        }

        particles.forEach(particle => {
          const age = elapsed - revealAt
          const progress = clamp(age / particle.life)
          if (progress >= 1) return
          const secondsAlive = age / 1000
          const x = cx + Math.cos(particle.angle) * particle.speed * secondsAlive
          const y = cy
            + Math.sin(particle.angle) * particle.speed * secondsAlive
            + 100 * secondsAlive * secondsAlive
          context.beginPath()
          context.arc(x, y, particle.size * (1 - progress * 0.6), 0, Math.PI * 2)
          context.globalAlpha = 1 - progress
          context.fillStyle = particle.color
          context.fill()
        })

        const toyProgress = easeOut(clamp((revealProgress - 0.06) * 3.2))
        if (image.complete && image.naturalWidth > 0 && toyProgress > 0) {
          const maxSize = Math.min(150, width * 0.48, height * 0.32)
          const scale = maxSize / Math.max(image.naturalWidth, image.naturalHeight)
          const drawWidth = image.naturalWidth * scale * toyProgress
          const drawHeight = image.naturalHeight * scale * toyProgress
          context.globalAlpha = clamp((revealProgress - 0.06) * 7)
          context.shadowColor = config.primary
          context.shadowBlur = normalizedRarity >= 4 ? 28 : 16
          context.drawImage(image, cx - drawWidth / 2, cy - drawHeight / 2, drawWidth, drawHeight)
          context.shadowBlur = 0
        }
        context.restore()
        context.globalAlpha = 1
      }

      if (config.flash > 0 && elapsed >= flashAt && elapsed < revealAt + 90) {
        const flashProgress = elapsed < revealAt
          ? 1
          : 1 - clamp((elapsed - revealAt) / 90)
        context.fillStyle = `rgba(255, 255, 255, ${flashProgress})`
        context.fillRect(0, 0, width, height)
      }

      animationFrame = window.requestAnimationFrame(drawFrame)
    }

    image.crossOrigin = 'anonymous'
    image.src = imageSrc
    startedAtRef.current = performance.now()
    holdActiveRef.current = false
    lastHoldUiRef.current = { pressing: false, progress: 0 }
    setHoldVisual({ pressing: false, progress: 0 })
    emit('wait')
    schedule(() => {
      holdActiveRef.current = true
      holdStartedAt = performance.now()
      emit('hold')
    }, tellAt)
    animationFrame = window.requestAnimationFrame(drawFrame)

    return () => {
      disposed = true
      startPressRef.current = () => undefined
      releasePressRef.current = () => undefined
      clearWork()
    }
  }, [imageSrc, rarity, reducedMotion])

  const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!holdActiveRef.current) return
    event.preventDefault()
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      // Pointer capture недоступен на некоторых платформах — hold всё равно работает
    }
    startPressRef.current()
  }

  const handlePointerRelease = () => {
    releasePressRef.current()
  }

  const config = getRarityRevealConfig(rarity)
  const isReveal = phase === 'reveal'
  const isHold = phase === 'hold'
  const webApp = window.Telegram?.WebApp
  const safeAreaTop = Number(webApp?.safeAreaInset?.top || 0)
    + Number(webApp?.contentSafeAreaInset?.top || 0)

  const caption = phase === 'wait'
    ? 'капсула найдена'
    : isHold
      ? (holdVisual.pressing
        ? 'не отпускай…'
        : holdVisual.progress > 0.02 && holdVisual.progress < 1
          ? 'ещё немного'
          : 'держи, чтобы открыть')
      : ''
  const captionColor = isHold && holdVisual.progress > 0.55 ? config.primary : 'rgba(255, 255, 255, .7)'

  return (
    <button
      type="button"
      aria-label={isHold ? 'Удерживайте, чтобы открыть капсулу' : 'Анимация открытия фигурки'}
      data-phase={phase}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerRelease}
      onPointerCancel={handlePointerRelease}
      onPointerLeave={handlePointerRelease}
      style={{
        appearance: 'none',
        background: BACKGROUND,
        border: 0,
        color: '#FFFFFA',
        cursor: isHold ? 'pointer' : 'default',
        display: 'block',
        height: '100%',
        minHeight: 320,
        overflow: 'hidden',
        padding: 0,
        position: 'relative',
        touchAction: isHold ? 'none' : 'manipulation',
        width: '100%',
      }}
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{ display: 'block', height: '100%', inset: 0, position: 'absolute', width: '100%' }}
      />
      {isReveal && rarity > 1 && (
        <span
          aria-hidden="true"
          style={{
            color: config.primary,
            filter: `drop-shadow(0 0 14px ${config.primary})`,
            fontSize: rarity >= 4 ? 16 : 13,
            left: 12,
            lineHeight: 1.4,
            position: 'absolute',
            right: 12,
            textAlign: 'center',
            textTransform: 'uppercase',
            top: `max(clamp(88px, 11dvh, 112px), ${Math.max(34, safeAreaTop + 12)}px, calc(env(safe-area-inset-top) + 12px), calc(var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px) + 12px))`,
          }}
        >
          {config.name}{rarity >= 5 ? '!' : ''}
        </span>
      )}
      {caption && (
        <span
          aria-hidden="true"
          style={{
            bottom: 34,
            color: captionColor,
            fontFamily: "'ComplianceSans', sans-serif",
            fontSize: 13,
            left: 12,
            lineHeight: 1.5,
            pointerEvents: 'none',
            position: 'absolute',
            right: 12,
            textAlign: 'center',
            transition: 'color .3s',
          }}
        >
          {caption}
        </span>
      )}
    </button>
  )
}

export default memo(RarityReveal)
export type { RarityRevealProps }
